import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { buildGuidePendings, categoryLabel, formatGuidePendings, reasonLabel, type PendingExtras } from '../../utils/guidePendings';
import { fetchCsv, parseJson } from '../../utils/csv';
import { controlFile, packageFiles } from '../../data/world/package';
import { loadEventPositions } from '../../utils/eventPositions';
import type { WorldData } from '../../types/world';

/** Preguntas visibles antes de pulsar «Ver todas». */
const PREVIEW = 20;
const urlOf = (name: string) => packageFiles.find(f => f.filename === name)?.url;

/** Lee los archivos que el mundo cargado no incluye (31, 32 y la fila dataset de 00_control). */
async function loadExtras(): Promise<PendingExtras> {
  const errors: string[] = [];
  const get = async (name: string, url?: string) => {
    if (!url) {
      errors.push(`${name}: sin URL en el paquete`);
      return undefined;
    }
    try {
      const r = await fetchCsv(url);
      r.errors.slice(0, 3).forEach(e => errors.push(`${name}: ${e}`));
      return r.rows;
    } catch (e) {
      errors.push(`${name}: ${e instanceof Error ? e.message : 'error de descarga'}`);
      return undefined;
    }
  };
  const [ep, control] = await Promise.all([loadEventPositions(), get('00_control.csv', controlFile.url)]);
  errors.push(...ep.errors);
  const eventMonsters = ep.eventMonsters.length || !ep.errors.some(e => e.startsWith('31_')) ? ep.eventMonsters : undefined;
  const eventSpawnRules = ep.spawnRules.length || !ep.errors.some(e => e.startsWith('32_')) ? ep.spawnRules : undefined;
  let controlPending: unknown;
  if (control) {
    const ds = control.find(r => r.record_type === 'dataset');
    const details = ds?.details_json ? parseJson<Record<string, unknown>>(ds.details_json) : undefined;
    if (!ds) errors.push('00_control.csv: sin fila record_type = dataset');else if (!details) errors.push('00_control.csv: details_json de la fila dataset no es JSON válido');else if (details.pending_requirements === undefined) errors.push('00_control.csv: details_json no contiene pending_requirements');else controlPending = details.pending_requirements;
  }
  return {
    eventMonsters,
    eventSpawnRules,
    positions: ep.positions,
    controlPending,
    errors
  };
}

/** Lista consolidada de pendientes de toda la guía, con un único botón para copiarla. */
export function GuidePendings({
  w
}: {
  w: WorldData;
}) {
  const [extras, setExtras] = useState<PendingExtras | undefined>();
  useEffect(() => {
    let alive = true;
    loadExtras().then(x => alive && setExtras(x));
    return () => {
      alive = false;
    };
  }, []);
  const r = useMemo(() => extras ? buildGuidePendings(w, extras) : undefined, [w, extras]);
  const text = useMemo(() => r ? formatGuidePendings(r) : '', [r]);
  const [showAll, setShowAll] = useState(false);
  if (!r) {
    return <WorldSection id="pendientes" title="Pendientes de la guía" data-id="element-1284" data-mp="90ykyoztaxk6">
        <p className="text-sm text-gray-400" role="status" data-id="element-1285" data-mp="90712bfkuo9q">Leyendo 31_event_monsters, 32_event_spawn_rules y 00_control.csv…</p>
      </WorldSection>;
  }
  const admin = r.pendings.filter(p => p.needsAdmin);
  const internal = r.pendings.length - admin.length;
  const questionsText = [`PREGUNTAS PARA ADMINISTRACIÓN — MU VANGUARD (${admin.length})`, '', ...admin.map((p, i) => `${i + 1}. [${categoryLabel[p.category]}] ${p.name}\n   Pregunta: ${p.question}\n   Falta: ${p.missing}\n   Se sabe: ${p.known}\n   Motivo: ${reasonLabel[p.reason]} · Evidencia: ${p.evidence.join(', ')}\n   Clave: ${p.key}`)].join('\n');
  const counts = Object.entries(categoryLabel).map(([k, label]) => ({
    label,
    n: admin.filter(p => p.category === k).length
  })).filter(c => c.n);
  return <WorldSection id="pendientes" title="Pendientes de la guía" data-id="element-1286" data-mp="2yhxoo0e8pinp">
      <p className="text-sm text-gray-400" data-id="element-1287" data-mp="2yifqdcjo8p30">
        Calculado en este navegador con la misma lógica que las fichas. Cada fila es una sola pregunta, aunque el dato aparezca en varias fichas. Responde con la clave y se integrará como «Confirmado por administración» con fecha.
      </p>

      {r.extrasErrors.length > 0 && <ul className="mt-3 rounded-xl border border-red-400/30 p-3 text-sm text-red-200" data-id="element-1288" data-mp="sn5auy9fkbj2">
          {r.extrasErrors.map(e => <li key={e} data-id="element-1289" data-mp="ir1xsxd1ishc">{e}</li>)}
        </ul>}

      <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2" data-id="element-1290" data-mp="ip8u9qxq4odk">
        <p className="font-cinzel text-2xl font-bold text-white" data-id="element-1291" data-mp="nqy2kytkanxy">{admin.length} preguntas para administración</p>
        <p className="text-sm text-gray-400" data-id="element-1292" data-mp="9rf5r267az0y">
          {internal} problemas internos · {r.resolvedByAdmin.length} respondidos · catálogo {r.catalog.published} publicados de {r.catalog.raw} ({r.catalog.excluded.length} excluidos)
        </p>
      </div>
      <ul className="mt-3 flex flex-wrap gap-2 text-sm" data-id="element-1293" data-mp="292y7avp6d50l">
        {counts.map(c => <li key={c.label} className="rounded-full border border-white/10 px-3 py-1 text-gray-300" data-id="element-1294" data-mp="2j767jpf5d2pi">
            {c.label} <span className="font-mono font-semibold text-white" data-id="element-1295" data-mp="2e8brgv8kds93">{c.n}</span>
          </li>)}
      </ul>

      <div className="mt-4 flex flex-wrap gap-2" data-id="element-1296" data-mp="2rzzy4j01noio">
        <CopyTextButton text={questionsText} label={`Copiar las ${admin.length} preguntas`} data-id="element-1297" data-mp="2s1kw6ilkhpla" />
        <CopyTextButton text={text} label="Copiar informe completo" data-id="element-1298" data-mp="2n7gp7zcaxhk7" />
      </div>

      <h3 className="mt-6 text-sm font-semibold text-white" data-id="element-1299" data-mp="31wcx9wecyukt">
        {showAll ? `Las ${admin.length} preguntas` : `Primeras ${Math.min(PREVIEW, admin.length)} de ${admin.length} preguntas`}
      </h3>
      <ol className="mt-2 divide-y divide-white/5 rounded-2xl border border-white/10" data-id="element-1300" data-mp="3axssgwwlqq4g">
        {(showAll ? admin : admin.slice(0, PREVIEW)).map((p, i) => <li key={p.key} className="grid gap-1 px-4 py-3 text-sm md:grid-cols-[2rem_1fr]" data-id="element-1301" data-mp="1gjrusfcsg95y" data-mpi={i}>
            <span className="font-mono text-gray-500" data-id="element-1302" data-mp="1cizmbobqq8aq">{i + 1}</span>
            <div className="min-w-0" data-id="element-1303" data-mp="1cksvba9tt4lw">
              <p className="font-semibold text-white" data-id="element-1304" data-mp="3bnzm3j67medy">{p.name}</p>
              <p className="text-gray-300" data-id="element-1305" data-mp="3bpu9r58v8klq">{p.question}</p>
              <p className="mt-1 text-xs text-gray-500" data-id="element-1306" data-mp="3gnc9q64wnphl">
                {categoryLabel[p.category]} · {reasonLabel[p.reason]} · Evidencia: {p.evidence.join(', ')}
                {p.affects ? ` · afecta a ${p.affects}` : ''}
              </p>
              <p className="mt-1 flex flex-wrap gap-x-3 text-xs" data-id="element-1307" data-mp="3qie0f6etwzma">
                {p.links.filter(x => x.startsWith('/')).slice(0, 3).map(to => <Link key={to} to={to} className="text-[#D4AF37] underline underline-offset-4" data-id="element-1308" data-mp="3eq060ogwu4a">{to}</Link>)}
                <span className="font-mono text-gray-600" data-id="element-1309" data-mp="87j26gsjxoco">{p.key}</span>
              </p>
            </div>
          </li>)}
      </ol>
      {admin.length > PREVIEW && <button type="button" onClick={() => setShowAll(v => !v)} aria-expanded={showAll} className="mt-3 h-9 rounded-xl border border-white/10 px-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1310" data-mp="i68abhunbufq">
          {showAll ? `Mostrar solo las primeras ${PREVIEW}` : `Ver las ${admin.length} preguntas`}
        </button>}

      <h3 className="mt-6 text-sm font-semibold text-white" data-id="element-1311" data-mp="1xo4xwrvq830a">Secciones revisadas</h3>
      <ul className="mt-2 space-y-1 text-sm" data-id="element-1312" data-mp="1sq3c3z6x1pl4">
        {r.coverage.map(c => <li key={c.section} className={c.reviewed ? 'text-gray-300' : 'text-amber-200'} data-id="element-1313" data-mp="2qbilw0hg5o89">
            {c.reviewed ? 'Revisada' : 'No revisada'} · {c.section} <span className="text-gray-500" data-id="element-1314" data-mp="2lfk10mvbuocl">— {c.note}</span>
          </li>)}
      </ul>
    </WorldSection>;
}